import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "types/ServiceStoreTypes";
import { getAgentMethods } from "../AgentChat/aiAgentMethods";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";

import { isSessionEnded, sessionMark } from "stores/sessionGuard";
/**
 * Route ViewStore for AI → Agent Admin.
 *
 * The chat module is a client for the seeded agent (catalog row `isa-hq`: Rosie); this is the
 * platform behind it — what it costs, what it is told, what it may call, what
 * it knows, and what it does unattended.
 *
 * ── WIRED TO THE PACKAGE AS IT IS, NOT AS IT WAS GUESSED ──────────────────
 *
 * The first cut of this store was written against field names and methods the
 * package does not have: it called `find` on a service whose only method is
 * `report`, patched a `status` onto proposed actions (which have no `patch` —
 * the decision is `approve`/`reject`, a compare-and-swap on 'pending'), sent
 * `isEnabled` to a schema that says `enabled`, and looked for a status of
 * 'proposed' where the package writes 'pending'. Every one of those was a
 * request that failed or a button that never rendered. The shapes below were
 * read from `@intrasync/ai-agent-api`'s own `.d.ts` files.
 *
 * ── READ-MOSTLY, ON PURPOSE ───────────────────────────────────────────────
 *
 * Every tab loads and displays. The writes are the places a human genuinely
 * decides something: approving or rejecting a proposed action, switching a
 * schedule or a budget on or off, and setting a budget in the first place —
 * without one, spend is unbounded and only visible after the fact.
 *
 * Reads are pulled per tab, on first open. Loading all five on entry would
 * fire a dozen finds for tabs nobody looked at.
 */

export type AgentAdminTab =
  | "usage"
  | "prompts"
  | "knowledge"
  | "automation"
  | "activity";

export const AGENT_ADMIN_TABS: AgentAdminTab[] = [
  "usage",
  "prompts",
  "knowledge",
  "automation",
  "activity",
];

/** Rows are shaped by the package, not by HQ — kept loose on purpose. */
type Row = Record<string, unknown>;

/** `ai-agent-usage-report` `report()` — grand totals plus one row per group. */
export interface UsageTotals {
  runs: number;
  completed?: number;
  failed?: number;
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
}

/** How far back the Usage tab looks. */
export const USAGE_WINDOW_DAYS = 30;

export interface BudgetForm {
  period: "month" | "day";
  maxRuns: string;
  maxTotalTokens: string;
  enforcement: "hard" | "soft";
}

const EMPTY_BUDGET: BudgetForm = {
  period: "month",
  maxRuns: "",
  maxTotalTokens: "",
  enforcement: "soft",
};

/** A run row with the two derived numbers the Activity table shows. */
const decorateRun = (run: Row): Row => {
  const usage = run.usage as { totalTokens?: number } | null | undefined;
  const started = typeof run.startedAt === "string" ? Date.parse(run.startedAt) : NaN;
  const ended = typeof run.endedAt === "string" ? Date.parse(run.endedAt) : NaN;
  return {
    ...run,
    totalTokens: usage?.totalTokens ?? null,
    durationMs:
      Number.isFinite(started) && Number.isFinite(ended) ? Math.max(0, ended - started) : null,
  };
};

/** Section key for the usage report (a custom method, not a find). */
export const USAGE_REPORT_PATH = "ai-agent-usage-report";

export default class AgentAdminStore {
  rootStore: RootStore;

  tab: AgentAdminTab = "usage";
  loading = false;
  error: string | null = null;

  /** Which tabs have already pulled, so switching back is instant. */
  private loaded = new Set<AgentAdminTab>();

  /**
   * Per-section load failures, keyed by service path (the usage report by
   * "ai-agent-usage-report"). `error` is one banner for the page; this is what
   * lets the ONE failed section say "unavailable" instead of its empty-state
   * sentence ("No budgets configured — spend is unbounded") while its
   * siblings still render. Cleared per path when that path next loads.
   */
  sectionErrors = new Map<string, string>();

  usage: Row[] = [];
  usageTotals: UsageTotals | null = null;
  budgets: Row[] = [];
  prompts: Row[] = [];
  tools: Row[] = [];
  knowledgeBases: Row[] = [];
  documents: Row[] = [];
  ingestionJobs: Row[] = [];
  scheduledRuns: Row[] = [];
  triggers: Row[] = [];
  proposedActions: Row[] = [];
  runs: Row[] = [];
  traceSpans: Row[] = [];

  budgetForm: BudgetForm = { ...EMPTY_BUDGET };
  savingBudget = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  private service(path: string): ServiceStoreType | null {
    const mgr = this.rootStore.serviceStoreManager as
      | { getStore?: (p: string) => unknown }
      | undefined;
    return (mgr?.getStore?.(path) as ServiceStoreType | null) ?? null;
  }

  /** The Feathers client, for the package's custom methods. */
  private get app(): { get?: (key: string) => unknown } | null {
    return ((this.rootStore as unknown as { app?: { get?: (key: string) => unknown } }).app ?? null);
  }

  /**
   * Admin only. This used to say operator+, mirroring a UI-side guess; the API
   * now gates every write on this page at admin (ai-agent-access.ts), and a
   * control that 403s on use is worse than one that does not render.
   */
  get canManage(): boolean {
    const tier = (
      this.rootStore.authStore as { currentUser?: { tier?: string } } | undefined
    )?.currentUser?.tier;
    return tier === "admin";
  }

  setTab(tab: AgentAdminTab) {
    this.tab = tab;
    void this.loadTab(tab);
  }

  /**
   * Pull one service into an array.
   *
   * Returns [] on any failure rather than throwing: a tab is a grid of five
   * independent lists, and one unavailable service must degrade to an empty
   * section instead of blanking the other four. The failure is surfaced once,
   * on `error`, so it is visible without being fatal.
   */
  private async pull(path: string, query: Record<string, unknown> = {}): Promise<Row[]> {
    const svc = this.service(path);
    // Deliberately NOT a failure: an unregistered service (package not wired)
    // degrades to an empty section — pinned by "tolerates a service that is
    // not registered at all".
    if (!svc) return [];
    const ended = sessionMark(this.rootStore);
    try {
      // findOrThrow: the ServiceStore swallows a failed find (resolves null),
      // which made every failure read as an empty list with no banner at all.
      const res = (await findOrThrow(svc, { query: { $limit: 100, ...query } })) as
        | { data?: Row[] }
        | Row[]
        | null
        | undefined;
      // loadTab drops the whole tab when the session ended; nothing to record.
      if (ended()) return [];
      runInAction(() => {
        this.sectionErrors.delete(path);
      });
      return Array.isArray(res) ? res : (res?.data ?? []);
    } catch (err: unknown) {
      if (ended()) return [];
      const message = loadErrorMessage(err, `Could not load ${path}`);
      runInAction(() => {
        this.error = message;
        this.sectionErrors.set(path, message);
      });
      return [];
    }
  }

  /**
   * The usage report. `ai-agent-usage-report` has ONE method, `report`, and it
   * is a custom method — `find` answers MethodNotAllowed, which is what the
   * Usage tab used to show on every load. Grouped by day and model so the
   * table answers "what did which model cost this month" rather than one
   * grand total.
   */
  private async pullUsage(): Promise<{ groups: Row[]; totals: UsageTotals | null }> {
    const handle = getAgentMethods(this.app, "ai-agent-usage-report", "report");
    if (!handle) return { groups: [], totals: null };
    const to = new Date();
    const from = new Date(to.getTime() - USAGE_WINDOW_DAYS * 24 * 60 * 60 * 1000);
    const ended = sessionMark(this.rootStore);
    try {
      const res = (await handle.call("report", {
        from: from.toISOString(),
        to: to.toISOString(),
        groupBy: ["day", "modelId"],
      })) as { totals?: UsageTotals; groups?: Row[] } | undefined;
      if (ended()) return { groups: [], totals: null };
      runInAction(() => {
        this.sectionErrors.delete(USAGE_REPORT_PATH);
      });
      return { groups: res?.groups ?? [], totals: res?.totals ?? null };
    } catch (err: unknown) {
      if (ended()) return { groups: [], totals: null };
      const message = loadErrorMessage(err, "Could not load the usage report");
      runInAction(() => {
        this.error = message;
        this.sectionErrors.set(USAGE_REPORT_PATH, message);
      });
      return { groups: [], totals: null };
    }
  }

  async loadTab(tab: AgentAdminTab, force = false): Promise<void> {
    if (!force && this.loaded.has(tab)) return;
    // A sign-out while the tab loads: the pulls above answered [] (the session
    // guard rejected them), and writing those — or latching the tab as loaded —
    // would show the next person an empty, "loaded" tab that never fetched.
    const ended = sessionMark(this.rootStore);
    runInAction(() => {
      this.loading = true;
      this.error = null;
    });

    try {
      if (tab === "usage") {
        const [usage, budgets] = await Promise.all([
          this.pullUsage(),
          this.pull("ai-agent-usage-budgets"),
        ]);
        if (ended()) return;
        runInAction(() => {
          this.usage = usage.groups;
          this.usageTotals = usage.totals;
          this.budgets = budgets;
        });
      } else if (tab === "prompts") {
        const [prompts, tools] = await Promise.all([
          this.pull("ai-agent-prompts"),
          this.pull("ai-agent-tools"),
        ]);
        if (ended()) return;
        runInAction(() => {
          this.prompts = prompts;
          this.tools = tools;
        });
      } else if (tab === "knowledge") {
        const [bases, documents, jobs] = await Promise.all([
          this.pull("ai-agent-knowledge-bases"),
          this.pull("ai-agent-documents"),
          this.pull("ai-agent-ingestion-jobs"),
        ]);
        if (ended()) return;
        runInAction(() => {
          this.knowledgeBases = bases;
          this.documents = documents;
          this.ingestionJobs = jobs;
        });
      } else if (tab === "automation") {
        const [scheduled, triggers, proposed] = await Promise.all([
          this.pull("ai-agent-scheduled-runs"),
          this.pull("ai-agent-agent-triggers"),
          this.pull("ai-agent-proposed-actions", { $sort: { createdAt: -1 } }),
        ]);
        if (ended()) return;
        runInAction(() => {
          this.scheduledRuns = scheduled;
          this.triggers = triggers;
          this.proposedActions = proposed;
        });
      } else {
        const [runs, spans] = await Promise.all([
          // Newest first: an admin opening this is looking at what just
          // happened, not at the beginning of time.
          this.pull("ai-agent-runs", { $sort: { createdAt: -1 }, $limit: 50 }),
          this.pull("ai-agent-trace-spans", { $sort: { startedAt: -1 }, $limit: 100 }),
        ]);
        if (ended()) return;
        runInAction(() => {
          this.runs = runs.map(decorateRun);
          this.traceSpans = spans;
        });
      }
      this.loaded.add(tab);
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /** The load error for one section's service path, or null when it loaded. */
  sectionError(path: string): string | null {
    return this.sectionErrors.get(path) ?? null;
  }

  /** Re-pull the tab in view — what the header's refresh button does. */
  async reload(): Promise<void> {
    await this.loadTab(this.tab, true);
  }

  /** Spans belonging to one run, oldest first — a run reads as a sequence. */
  spansForRun(runId: string): Row[] {
    return this.traceSpans
      .filter((s) => s.runId === runId)
      .sort((a, b) => String(a.startedAt ?? "").localeCompare(String(b.startedAt ?? "")));
  }

  /** Proposed actions still waiting on a person. The package's word is 'pending'. */
  get pendingProposedActions(): Row[] {
    return this.proposedActions.filter((r) => r.status === "pending");
  }

  /**
   * Approve or reject a proposed action.
   *
   * The one genuinely human decision on this page. The package exposes it as
   * two CUSTOM methods, `approve` and `reject` — a compare-and-swap on
   * 'pending', so a decision racing another resolves to exactly one — and has
   * no `patch` at all. The id travels as the data argument (aiAgentMethods.ts).
   */
  async decideProposedAction(id: string, approved: boolean): Promise<boolean> {
    if (!this.canManage) return false;
    const handle = getAgentMethods(this.app, "ai-agent-proposed-actions", "approve", "reject");
    if (!handle) {
      runInAction(() => {
        this.error = "Decisions need the live connection; reload the page.";
      });
      return false;
    }
    try {
      await handle.call(approved ? "approve" : "reject", id, {}, {});
      await this.loadTab("automation", true);
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = err instanceof Error ? err.message : "Could not record that decision";
      });
      return false;
    }
  }

  /** Turn a schedule on or off without deleting it. The field is `enabled`. */
  async setScheduleEnabled(id: string, enabled: boolean): Promise<boolean> {
    return this.patchEnabled("ai-agent-scheduled-runs", id, enabled, "automation");
  }

  /** Same switch, for a budget. */
  async setBudgetEnabled(id: string, enabled: boolean): Promise<boolean> {
    return this.patchEnabled("ai-agent-usage-budgets", id, enabled, "usage");
  }

  private async patchEnabled(
    path: string,
    id: string,
    enabled: boolean,
    tab: AgentAdminTab,
  ): Promise<boolean> {
    if (!this.canManage) return false;
    const svc = this.service(path);
    if (!svc) return false;
    try {
      await (svc as unknown as { patch?: (id: string, data: object) => Promise<unknown> }).patch?.(
        id,
        { enabled },
      );
      await this.loadTab(tab, true);
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = err instanceof Error ? err.message : "Could not change that setting";
      });
      return false;
    }
  }

  // ---- Budgets ------------------------------------------------------------
  //
  // The package enforces `hard`/`soft` budgets in its run loop for free — but
  // only if a row exists, and nothing created one. "No budgets configured —
  // spend is unbounded" was the honest empty state; this is the way out of it.

  setBudgetField<K extends keyof BudgetForm>(field: K, value: BudgetForm[K]) {
    this.budgetForm[field] = value;
  }

  /** What the form would create, or null when it names no limit at all. */
  get budgetPayload(): {
    agentId: null;
    period: BudgetForm["period"];
    maxRuns: number | null;
    maxTotalTokens: number | null;
    enforcement: BudgetForm["enforcement"];
    enabled: true;
  } | null {
    const num = (v: string): number | null => {
      const n = Number(v.trim());
      return v.trim() && Number.isInteger(n) && n > 0 ? n : null;
    };
    const maxRuns = num(this.budgetForm.maxRuns);
    const maxTotalTokens = num(this.budgetForm.maxTotalTokens);
    if (maxRuns === null && maxTotalTokens === null) return null;
    return {
      // Company-wide: the seeded office manager is the only agent, and a per-agent
      // budget needs an agent id an admin has no way to type here.
      agentId: null,
      period: this.budgetForm.period,
      maxRuns,
      maxTotalTokens,
      enforcement: this.budgetForm.enforcement,
      enabled: true,
    };
  }

  get canSubmitBudget(): boolean {
    return this.canManage && this.budgetPayload !== null && !this.savingBudget;
  }

  async submitBudget(): Promise<boolean> {
    const payload = this.budgetPayload;
    if (!payload || !this.canSubmitBudget) return false;
    const svc = this.service("ai-agent-usage-budgets");
    if (!svc) return false;
    this.savingBudget = true;
    try {
      await svc.create?.(payload as never);
      runInAction(() => {
        this.budgetForm = { ...EMPTY_BUDGET };
      });
      await this.loadTab("usage", true);
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = err instanceof Error ? err.message : "Could not create the budget";
      });
      return false;
    } finally {
      runInAction(() => {
        this.savingBudget = false;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.tab = "usage";
    this.loading = false;
    this.error = null;
    this.loaded = new Set<AgentAdminTab>();
    this.sectionErrors = new Map<string, string>();
    this.usage = [];
    this.usageTotals = null;
    this.budgets = [];
    this.prompts = [];
    this.tools = [];
    this.knowledgeBases = [];
    this.documents = [];
    this.ingestionJobs = [];
    this.scheduledRuns = [];
    this.triggers = [];
    this.proposedActions = [];
    this.runs = [];
    this.traceSpans = [];
    this.budgetForm = { ...EMPTY_BUDGET };
    this.savingBudget = false;
  }
}

/**
 * One store per RootStore, created on first use.
 *
 * Matches AgentChatStore: this module has no `*StoreList`, so its view stores
 * are not registered through appStoresConfig. A WeakMap rather than a module
 * singleton so a torn-down RootStore (tests, a re-login) does not leak its
 * store or hand the next one stale rows.
 */
const instances = new WeakMap<object, AgentAdminStore>();

export const getAgentAdminStore = (rootStore: RootStore): AgentAdminStore => {
  const key = rootStore as unknown as object;
  let store = instances.get(key);
  if (!store) {
    store = new AgentAdminStore(rootStore);
    instances.set(key, store);
    // Not an app store, so purgeUserData would never reach it otherwise.
    rootStore.registerSessionStore("agentAdminStore", store);
  }
  return store;
};
